/* Copyright (c) 2022 Xperi. All Rights Reserved. */

import { RecordingStatus } from 'client-code/previews';
import { TRecordingOverlayIds } from 'src/core/types';

import { Constants } from '../../core/constants';
import { DeviceFeatures } from '../../core/device';
import { GuideType, InteractiveComponents } from '../../core/enums';
import { LocaleKeys } from '../../core/locale';
import { Log } from '../../core/logging/Log';
import { ScreenTaskControllerTypes } from '../../core/navigation/ScreenTaskControllerTypes';
import { PlaybackContentType } from '../../core/playback/enums';
import { SharedPrefConstants, SharedPreferences } from '../../core/storage';
import { Storage } from '../../core/storage/Storage';
import { ChannelsType } from '../../core/types/TChannelsType';
import { NdvrPlaybackType } from '../../core/videoplayer/enums';
import { IPlayable } from '../../core/videoplayer/interfaces';
import { VideoPlaybackController } from '../../core/videoplayer/VideoPlaybackController';
import {
    onKeyOkEmptyGuide,
    onMouseClickEmptyGuide,
} from '../guide/emptyguide/emptyguide.navigation.reducers';
import {
    onKeyBackChannelOptionsOverlay,
    onKeyOkChannelOptionsOverlayOneLineGuide,
    onMouseClickChannelOptionsOverlay,
} from '../overlay/channeloptionsoverlay/channeloptionsoverlay.reducers';
import {
    onKeyOkCreateOnePassOverlay,
    onMouseClickCreateOnePassOverlay,
} from '../overlay/createonepassoverlay/createonepassoverlay.reducres';
import {
    onKeyBackJumpChannelOverlay,
    onKeyOkJumpChannelOverlay,
    onMouseClickJumpChannelOverlay,
} from '../overlay/jumpchanneloverlay/jumpchanneloverlay.reducers';
import {
    onKeyOkModifyOnePassOverlay,
    onMouseClickModifyOnePassOverlay,
} from '../overlay/modifyonepassoverlay/modifyonepassoverlay.reducers';
import {
    clearOnePassSharedPreference,
    onKeyLeftOnePassOptionsOverlay,
    onKeyOkDOCOverlay,
    onKeyOkOnePassOptionsOverlay,
    onKeyRightOnePassOptionsOverlay,
    onMouseClickDOCOptionsOverlay,
    onMouseClickOnePassOptionsOverlay,
} from '../overlay/onepassoptionsoverlay/onepassoptionsoverlay.reducers';
import { OverlayTypes } from '../overlay/OverlayTypes';
import { resetPCPinValuesAction } from '../overlay/pcpinoverlay/pcpinoverlay.actions';
import {
    hidePcPinOverlay,
    onKeyBackPcPinOverlay,
    onKeyDigitPcPinOverlay,
    onKeyDownPcPinOverlay,
    onKeyLeftPcPinOverlay,
    onKeyOkPcPinOverlay,
    onKeyRightPcPinOverlay,
    onKeyUpPcPinOverlay,
    onMouseClickPcPinOverlay,
    onMouseOutPcPinOverlay,
    onMouseOverPcPinOverlay,
} from '../overlay/pcpinoverlay/pcpinoverlay.reducers';
import {
    clearScheduleRecordingSharedPreference,
    onKeyLeftRecordingOverlay,
    onKeyOkRecordingOverlay,
    onKeyOkStopOrCancelRecording,
    onKeyRightRecordingOverlay,
    onMouseClickRecordingOverlay,
    onMouseClickStopOrCancelRecording,
} from '../overlay/recordingoverlay/recordingoverlay.reducers';
import {
    clearBookmarkSharedPreference,
    clearDiskSpaceSharedPreference,
    onKeyBackRecordOverlay,
    onKeyOkRecordOverlay,
    onMouseClickRecordOverlay,
    onMouseWheelRecordOverlay,
} from '../overlay/recordoverlay/recordoverlay.reducers';
import { onKeyOkSocuErrorOverlay } from '../overlay/socuerroroverlay/socuerroroverlay.reducres';
import {
    onKeyOkStorageLimitOverlay,
    onMouseClickStorageLimitOverlay,
} from '../overlay/storagelimitoverlay/storagelimitoverlay.reducers';
import {
    onKeyBackVodResumeStartOverOverlay,
    onKeyOkVodResumeStartOverOverlayFromLiveTv,
    prepareVodResumeStartOverOverlayFromLiveTv,
} from '../overlay/vodresumestartoveroverlay/vodresumestartoveroverlay.reducers';
import {
    hideOverlayDialog,
    onKeyDownOverlayDialog,
    onKeyLeftOverlayDialog,
    onKeyRightOverlayDialog,
    onKeyUpOverlayDialog,
    onMouseOutOverlayDialogOperations,
    onMouseOverOverlayDialogOperations,
} from '../overlaydialog/overlaydialog.reducers';
import { setScreenTaskController, showPreviousScreen } from '../screens/screens.reducers';
import { hideSpinner } from '../spinner/spinner.reducers';
import {
    hideActiveOverlays,
    isPlayPauseRestricted,
    onKeyAdvanceVideoPlayer,
    onKeyBackDefaultVideoPlayer,
    onKeyBackVideoPlayer,
    onKeyChannelDownVideoPlayer,
    onKeyChannelUpVideoPlayer,
    onKeyFastForwardVideoPlayer,
    onKeyLeftVideoPlayer,
    onKeyPauseVideoPlayer,
    onKeyPlayVideoPlayer,
    onKeyReplayVideoPlayer,
    onKeyRewindVideoPlayer,
    onKeyRightVideoPlayer,
    onKeyStopVideoPlayer,
} from '../videoplayer/navigation/videoplayernavigation.reducers';
import {
    onKeyBackAudioTrackOverlay,
    onKeyOkAudioTrackOverlay,
} from '../videoplayer/overlays/audiotrackoverlay/audiotrackoverlay.reducers';
import {
    onKeyBackDeleteRecordingOverlay,
    onKeyOkDeleteRecordingOverlay,
} from '../videoplayer/overlays/deleterecording/deleterecording.reducers';
import {
    onKeyBackErrorOverlay,
    onKeyChanelUpErrorOverlay,
    onKeyChannelDownErrorOverlay,
    onKeyOkErrorOverlay,
} from '../videoplayer/overlays/erroroverlay/erroroverlay.reducers';
import {
    onKeyBackSubtitleAndCCLanguageOverlay,
    onKeyOkSubtitleAndCCLanguageOverlay,
} from '../videoplayer/overlays/subtitleandcclanguageoverlay/subtitleandcclanguageoverlay.reducers';
import {
    hideStillThereOsd,
    isAdultContentPlaybackActive,
    resetPlaybackParamsVideoplayer,
    showPCPinOverlayForVideoPlayer,
} from '../videoplayer/videoplayer.reducers';
import {
    onKeyBackFullInfoBanner,
    onKeyChannelDownFullInfoBanner,
    onKeyChannelUpFullInfoBanner,
    onKeyDownFullInfoBanner,
    onKeyLeftFullInfoBanner,
    onKeyOkFullInfoBanner,
    onKeyRightFullInfoBanner,
} from './infobanner/fullinfobannernavigation.reducers';
import {
    setCurrentActiveIndexAction,
    setMediaButtonMouseOverIndex,
} from './infobanner/infobanner.actions';
import {
    hideFullInfo,
    hideInfoBanners,
    hideStandardInfo,
    hideTrickPlayMenu,
    setActionsStripHighlightBorderActive,
    setEnteringChannelNumberEnabled,
    showFullInfo,
    showStandardInfo,
    showTrickPlayMenu,
    updateFullInfoBannerTimer,
    updateStandardInfoBannerTimer,
    updateTrickPlayMenuTimer,
} from './infobanner/infobanner.reducers';
import { MediaButtonTypes } from './infobanner/mediabuttontypes';
import { PlaybackState, TrickPlayType } from './infobanner/PlaybackTypes';
import {
    onKeyBackStandardInfoBanner,
    onKeyChannelDownStandardInfoBanner,
    onKeyChannelUpStandardInfoBanner,
    onKeyInfoStandardInfoBanner,
    onKeyLeftStandardInfoBanner,
    onKeyOkStandardInfoBanner,
    onKeyRightStandardInfoBanner,
} from './infobanner/standardinfobannernavigation.reducers';
import {
    clearLongPressIntervalTrickplayMenu,
    onKeyBackTrickplayMenu,
    onKeyChannelDownTrickplayMenu,
    onKeyChannelUpTrickplayMenu,
    onKeyInfoTrickplayMenu,
    onKeyLeftTrickplayMenu,
    onKeyLongLeftTrickplayMenu,
    onKeyLongRightTrickplayMenu,
    onKeyOkTrickplayMenu,
    onKeyRightTrickplayMenu,
} from './infobanner/trickplaymenunavigation.reducers';
import {
    ONE_LINE_GUIDE_ABOVE_CHANNEL_SLICE_SIZE,
    ONE_LINE_GUIDE_BELOW_CHANNEL_SLICE_SIZE,
} from './onelineguide/onelineguide.constants';
import {
    onKeyDownOneLineGuide,
    onKeyLeftOneLineGuide,
    onKeyOkOneLineGuide,
    onKeyRightOneLineGuide,
    onKeyUpOneLineGuide,
    onLongClickOneLineGuide,
    onLongClickStopOneLineGuide,
    onLongKeyDownOneLineGuide,
    onLongKeyOkOneLineGuide,
    onLongKeyStopOneLineGuide,
    onLongKeyUpOneLineGuide,
    onMouseClickOneLineGuide,
    onMouseWheelOneLineGuide,
} from './onelineguide/onelineguide.navigation.reducers';
import { hideOneLineGuide, showOneLineGuide } from './onelineguide/onelineguide.reducers';
import {
    onMouseClickRootInteractiveLayerOperations,
    onMouseClickVideoPlayerUiInteractiveLayerOperations,
    onMouseOutVideoPlayerUiOperations,
    onMouseOverVideoPlayerUiOperations,
} from './videoplayeruimouseoperations.reducers';

const TAG = 'videoplayeruinavigation';

const DEBOUNCE_DELAY: number = 800; // 800 milli second debounce delay

let debounceTimer: NodeJS.Timeout | null = null;

let fastZappingStatusTimer: NodeJS.Timeout | null = null;

let isUserFastZapping: boolean = false;

let noOfKeysPressed: number = 0;

/**
 * The overlay saving channel options decided to stash away locale strings in storage
 * instead of proper ChannelsType values.
 * Since the data layer should stick to actual data (and not get caught up in locale drama),
 * this little helper steps in as the translator between the two — t
 * urning those fancy locale keys into the ChannelsType they should've been all along.
 */
const getChannelsType = (): ChannelsType => {
    const channelsTypeFromStorage = Storage.getInstance().getItem(Storage.Keys.CHANNELS_TYPE);

    switch (channelsTypeFromStorage) {
        case LocaleKeys.GUIDE_CHANNEL_TYPES_ALL_CHANNELS:
            return ChannelsType.ALL;
        case LocaleKeys.GUIDE_CHANNEL_TYPES_FAVORITES:
            return ChannelsType.FAVORITE;
        default:
            return ChannelsType.SUBSCRIBED;
    }
};

/**
 * Warning: Unless you're a mind reader, leave this magic alone!
 * If the user presses a single key, the channel *must* change instantly, because, obviously,
 * who has time for 800ms of waiting?
 * But if they press more than one key (because, you know, humans are unpredictable),
 * we'll kindly debounce and wait 800ms.
 */
const handleChannelChangeWithFastZap =
    (channelStep: number) => (dispatch: Function, getState: Function) => {
        // Clear existing debounce timers
        if (debounceTimer) clearTimeout(debounceTimer);

        const videoController = VideoPlaybackController.getInstance();

        const { isActionsStripActive, isFullInfoActive, isTrickplayMenuActive } =
            getState().infoBanner;

        noOfKeysPressed += channelStep;

        // Stop playback if the user is fast zapping
        if (isUserFastZapping) {
            videoController.stop();
            Log.d(TAG, 'Playback stopped as user is fast zapping');
        }
        // Handle UI Elements
        if (!isFullInfoActive && !isTrickplayMenuActive) {
            dispatch(showStandardInfo());
            dispatch(updateStandardInfoBannerTimer());
        } else if (isTrickplayMenuActive) {
            dispatch(updateStandardInfoBannerTimer());
            dispatch(updateTrickPlayMenuTimer());
        } else if (isFullInfoActive || isActionsStripActive) {
            dispatch(updateFullInfoBannerTimer());
        }

        dispatch(hideSpinner());
        dispatch(hideActiveOverlays());

        if (isUserFastZapping) {
            if (fastZappingStatusTimer) {
                clearTimeout(fastZappingStatusTimer);
            }

            // Set a debounce timer for fast zapping actions
            debounceTimer = setTimeout(() => {
                Log.d(TAG, 'Channel up after debounce. noOfKeysPressed =', noOfKeysPressed);
                if (noOfKeysPressed > 0) dispatch(handleKeyChannelUp());
                else dispatch(handleKeyChannelDown());
                noOfKeysPressed = 0;
            }, DEBOUNCE_DELAY);
        } else {
            Log.d(TAG, 'Channel up after single press. noOfKeysPressed =', noOfKeysPressed);
            if (noOfKeysPressed > 0) dispatch(handleKeyChannelUp());
            else dispatch(handleKeyChannelDown());
            noOfKeysPressed = 0;
            isUserFastZapping = true;
            // Reset fast zapping status
            fastZappingStatusTimer = setTimeout(() => {
                isUserFastZapping = false;
            }, DEBOUNCE_DELAY);
        }
    };

const handleKeyChannelDown = () => (dispatch: Function, getState: Function) => {
    const { screenTaskController } = getState().screens;
    const channelType: ChannelsType = getChannelsType();

    switch (screenTaskController) {
        case ScreenTaskControllerTypes.DEFAULT_VIDEO_PLAYER_TASKS:
            dispatch(showStandardInfo());
            dispatch(onKeyChannelDownVideoPlayer(Math.abs(noOfKeysPressed) || 1, channelType));
            break;

        case ScreenTaskControllerTypes.FULL_INFO:
            dispatch(onKeyChannelDownFullInfoBanner());
            dispatch(onKeyChannelDownVideoPlayer(Math.abs(noOfKeysPressed) || 1, channelType));
            break;

        case ScreenTaskControllerTypes.TRICKPLAY_MENU:
            dispatch(onKeyChannelDownTrickplayMenu());
            dispatch(onKeyChannelDownVideoPlayer(Math.abs(noOfKeysPressed) || 1, channelType));
            break;

        case ScreenTaskControllerTypes.STANDARD_INFO:
            dispatch(onKeyChannelDownStandardInfoBanner());
            dispatch(onKeyChannelDownVideoPlayer(Math.abs(noOfKeysPressed) || 1, channelType));
            break;

        case ScreenTaskControllerTypes.PLAYER_ERROR_OVERLAY:
            dispatch(onKeyChannelDownErrorOverlay());
            dispatch(showStandardInfo());
            dispatch(onKeyChannelDownVideoPlayer(Math.abs(noOfKeysPressed) || 1, channelType));
            break;

        default:
            break;
    }
};

const handleKeyChannelUp = () => (dispatch: Function, getState: Function) => {
    const channelType: ChannelsType = getChannelsType();
    const { screenTaskController } = getState().screens;
    switch (screenTaskController) {
        case ScreenTaskControllerTypes.DEFAULT_VIDEO_PLAYER_TASKS:
            dispatch(showStandardInfo());
            dispatch(onKeyChannelUpVideoPlayer(Math.abs(noOfKeysPressed) || 1, channelType));
            break;

        case ScreenTaskControllerTypes.FULL_INFO:
            dispatch(onKeyChannelUpFullInfoBanner());
            dispatch(onKeyChannelUpVideoPlayer(Math.abs(noOfKeysPressed) || 1, channelType));
            break;

        case ScreenTaskControllerTypes.TRICKPLAY_MENU:
            dispatch(onKeyChannelUpTrickplayMenu());
            dispatch(onKeyChannelUpVideoPlayer(Math.abs(noOfKeysPressed) || 1, channelType));
            break;

        case ScreenTaskControllerTypes.STANDARD_INFO:
            dispatch(onKeyChannelUpStandardInfoBanner());
            dispatch(onKeyChannelUpVideoPlayer(Math.abs(noOfKeysPressed) || 1, channelType));
            break;

        case ScreenTaskControllerTypes.PLAYER_ERROR_OVERLAY:
            dispatch(onKeyChanelUpErrorOverlay());
            dispatch(showStandardInfo());
            dispatch(onKeyChannelUpVideoPlayer(Math.abs(noOfKeysPressed) || 1, channelType));
            break;
        default:
            break;
    }
};

export const clearDebounceTimerVideoPlayerUi = () => {
    if (debounceTimer) {
        clearTimeout(debounceTimer);
        Log.d(TAG, 'Timeout cleared: debounceTimer');
        noOfKeysPressed = 0;
        isUserFastZapping = false;
    }
    if (fastZappingStatusTimer) {
        clearTimeout(fastZappingStatusTimer);
        Log.d(TAG, 'Timeout cleared: fastZappingStatusTimer');
    }
};

export const swtichToPreviousControllerFromInteractiveLayer =
    () => (dispatch: Function, getState: Function) => {
        const { screenTaskController, previousScreenTaskController } = getState().screens;
        if (screenTaskController === ScreenTaskControllerTypes.LIVE_TV_INTERACTIVE_LAYER_TASKS) {
            dispatch(setScreenTaskController(previousScreenTaskController));
        }
    };

export const onKeyBackVideoPlayerUi = () => (dispatch: Function, getState: Function) => {
    const { screenTaskController, previousScreenTaskController } = getState().screens;
    const { uiProperties } = getState().overlayDialog;
    const { overlayType } = uiProperties;

    switch (screenTaskController) {
        case ScreenTaskControllerTypes.AUDIO_TRACK_OVERLAY:
            dispatch(onKeyBackAudioTrackOverlay());
            break;

        case ScreenTaskControllerTypes.CHANNEL_OPTIONS_OVERLAY:
            dispatch(onKeyBackChannelOptionsOverlay());
            dispatch(showOneLineGuide());
            break;
        case ScreenTaskControllerTypes.CANCEL_RECORDING_OVERLAY_TASKS:
            dispatch(hideOverlayDialog());
            break;
        case ScreenTaskControllerTypes.CANCEL_ONE_PASS_OVERLAY_TASKS:
        case ScreenTaskControllerTypes.CREATE_ONE_PASS_OVERLAY_TASKS:
        case ScreenTaskControllerTypes.MODIFY_ONE_PASS_OVERLAY_TASKS:
        case ScreenTaskControllerTypes.ONE_PASS_OPTIONS_OVERLAY_TASK:
        case ScreenTaskControllerTypes.RECORDING_OVERLAY_TASKS:
        case ScreenTaskControllerTypes.STOP_RECORDING_OVERLAY_TASKS:
        case ScreenTaskControllerTypes.SLO_TASKS:
            clearBookmarkSharedPreference();
            clearDiskSpaceSharedPreference();
            clearOnePassSharedPreference();
            clearScheduleRecordingSharedPreference();
            dispatch(hideOverlayDialog());
            break;

        case ScreenTaskControllerTypes.DEFAULT_VIDEO_PLAYER_TASKS:
            dispatch(onKeyBackDefaultVideoPlayer());
            break;

        case ScreenTaskControllerTypes.DELETE_RECORDING_OVERLAY_TASKS:
            dispatch(onKeyBackDeleteRecordingOverlay());
            break;

        case ScreenTaskControllerTypes.JUMP_CHANNEL_OVERLAY_TASKS:
            dispatch(onKeyBackJumpChannelOverlay());
            break;

        case ScreenTaskControllerTypes.FULL_INFO:
        case ScreenTaskControllerTypes.LIVE_TV_INTERACTIVE_LAYER_TASKS:
            dispatch(onKeyBackFullInfoBanner());
            break;

        case ScreenTaskControllerTypes.ONE_LINE_GUIDE_TASKS:
            dispatch(hideOneLineGuide());
            break;

        case ScreenTaskControllerTypes.PC_PIN_OVERLAY_TASK:
            dispatch(onKeyBackPcPinOverlay());
            break;

        case ScreenTaskControllerTypes.SOCU_ERROR_OVERLAY_TASKS:
            dispatch(onKeyOkSocuErrorOverlay());
            break;

        case ScreenTaskControllerTypes.SUBTITLE_AND_CC_LANGUAGE_OVERLAY:
            dispatch(onKeyBackSubtitleAndCCLanguageOverlay());
            break;

        case ScreenTaskControllerTypes.TRICKPLAY_MENU:
            dispatch(onKeyBackTrickplayMenu());
            break;

        case ScreenTaskControllerTypes.STANDARD_INFO:
            dispatch(onKeyBackStandardInfoBanner());
            break;

        case ScreenTaskControllerTypes.RECORD_OVERLAY_TASKS:
            dispatch(onKeyBackRecordOverlay());
            dispatch(showOneLineGuide());
            break;

        case ScreenTaskControllerTypes.PLAYER_ERROR_OVERLAY:
            dispatch(onKeyBackErrorOverlay());
            dispatch(onKeyBackVideoPlayer());
            break;

        case ScreenTaskControllerTypes.JUMP_CHANNEL_ERROR_OVERLAY_TASKS:
            {
                dispatch(onKeyOkErrorOverlay());

                const wasErrorFromOneLineGuide =
                    overlayType === OverlayTypes.OLG_JUMP_CHANNEL_ERROR_OVERLAY;
                if (wasErrorFromOneLineGuide) {
                    dispatch(showOneLineGuide());
                } else {
                    dispatch(onKeyBackVideoPlayer());
                }
            }
            break;

        case ScreenTaskControllerTypes.STILL_THERE_OSD_TASKS:
            dispatch(hideStillThereOsd());
            break;

        case ScreenTaskControllerTypes.VOD_RESUME_START_OVER_OVERLAY_TASKS:
            dispatch(onKeyBackVodResumeStartOverOverlay());
            break;

        case ScreenTaskControllerTypes.PREPARING_VOD_RESUME_START_OVER_OVERLAY_TASKS:
            dispatch(setScreenTaskController(previousScreenTaskController));
            break;

        default:
            break;
    }
};

const isNdvrContentPlaying = (): boolean => {
    return VideoPlaybackController.getInstance().isNdvr();
};

const isVodContentPlaying = (): boolean => {
    return VideoPlaybackController.getInstance().isVod();
};

export const onKeyChannelDownVideoPlayerUi = () => (dispatch: Function, getState: Function) => {
    if (!VideoPlaybackController.getInstance().isNetworkConnected()) {
        return;
    }
    const { screenTaskController } = getState().screens;

    // If any new ScreenTask is added, Kindly verify if channel change need to be added or not
    switch (screenTaskController) {
        case ScreenTaskControllerTypes.DEFAULT_VIDEO_PLAYER_TASKS:
        case ScreenTaskControllerTypes.FULL_INFO:
        case ScreenTaskControllerTypes.TRICKPLAY_MENU:
        case ScreenTaskControllerTypes.STANDARD_INFO:
        case ScreenTaskControllerTypes.PLAYER_ERROR_OVERLAY:
        case ScreenTaskControllerTypes.JUMP_CHANNEL_OVERLAY_TASKS:
            if (isNdvrContentPlaying() || isVodContentPlaying()) {
                // Block key channel down for nDVR & VOD content.
                return;
            }
            dispatch(handleChannelChangeWithFastZap(-1));
            break;
        case ScreenTaskControllerTypes.ONE_LINE_GUIDE_TASKS:
            /*
             * When a user presses CHANNEL DOWN in OLG,
             * he should move to the bottom-most visible channel
             */
            dispatch(onKeyDownOneLineGuide(true, ONE_LINE_GUIDE_BELOW_CHANNEL_SLICE_SIZE));
            break;

        default:
            break;
    }
};

export const onKeyChannelUpVideoPlayerUi = () => (dispatch: Function, getState: Function) => {
    if (!VideoPlaybackController.getInstance().isNetworkConnected()) {
        return;
    }
    const { screenTaskController } = getState().screens;

    // If any new ScreenTask is added, Kindly verify if channel change is needed or not
    switch (screenTaskController) {
        case ScreenTaskControllerTypes.DEFAULT_VIDEO_PLAYER_TASKS:
        case ScreenTaskControllerTypes.FULL_INFO:
        case ScreenTaskControllerTypes.TRICKPLAY_MENU:
        case ScreenTaskControllerTypes.STANDARD_INFO:
        case ScreenTaskControllerTypes.PLAYER_ERROR_OVERLAY:
        case ScreenTaskControllerTypes.JUMP_CHANNEL_OVERLAY_TASKS:
            if (isNdvrContentPlaying() || isVodContentPlaying()) {
                // Block key channel up for nDVR & VOD content.
                return;
            }
            dispatch(handleChannelChangeWithFastZap(+1));
            break;
        case ScreenTaskControllerTypes.ONE_LINE_GUIDE_TASKS:
            /*
             * When a user presses CHANNEL UP in OLG,
             * he should move to the up-most visible channel
             */
            dispatch(onKeyUpOneLineGuide(true, ONE_LINE_GUIDE_ABOVE_CHANNEL_SLICE_SIZE));
            break;
        default:
            break;
    }
};

export const onKeyDigitVideoPlayerUi = () => (dispatch: Function, getState: Function) => {
    if (!VideoPlaybackController.getInstance().isNetworkConnected()) {
        return;
    }
    const { screenTaskController } = getState().screens;

    if (
        isNdvrContentPlaying() &&
        screenTaskController !== ScreenTaskControllerTypes.PC_PIN_OVERLAY_TASK
    ) {
        // Block digit entry for nDVR content, except when on the PIN overlay screen.
        return;
    }

    const { isStandardInfoActive, isActionsStripActive, isFullInfoActive } = getState().infoBanner;

    switch (screenTaskController) {
        case ScreenTaskControllerTypes.FULL_INFO:
            // In case the info banner is collapsed
            if (!isStandardInfoActive && !isFullInfoActive && isActionsStripActive) {
                dispatch(hideFullInfo());
                dispatch(showStandardInfo());
            }
            dispatch(setEnteringChannelNumberEnabled(true));
            break;
        case ScreenTaskControllerTypes.PC_PIN_OVERLAY_TASK:
            dispatch(onKeyDigitPcPinOverlay());
            break;
        case ScreenTaskControllerTypes.TRICKPLAY_MENU:
        case ScreenTaskControllerTypes.STANDARD_INFO:
            dispatch(setEnteringChannelNumberEnabled(true));
            break;

        case ScreenTaskControllerTypes.DEFAULT_VIDEO_PLAYER_TASKS:
            dispatch(showStandardInfo());
            dispatch(setEnteringChannelNumberEnabled(true));
            break;

        default:
            break;
    }
};

export const onKeyDownVideoPlayerUi = () => (dispatch: Function, getState: Function) => {
    const { screenTaskController } = getState().screens;

    switch (screenTaskController) {
        case ScreenTaskControllerTypes.AUDIO_TRACK_OVERLAY:
        case ScreenTaskControllerTypes.CANCEL_ONE_PASS_OVERLAY_TASKS:
        case ScreenTaskControllerTypes.CREATE_ONE_PASS_OVERLAY_TASKS:
        case ScreenTaskControllerTypes.CANCEL_RECORDING_OVERLAY_TASKS:
        case ScreenTaskControllerTypes.DELETE_RECORDING_OVERLAY_TASKS:
        case ScreenTaskControllerTypes.JUMP_CHANNEL_OVERLAY_TASKS:
        case ScreenTaskControllerTypes.MODIFY_ONE_PASS_OVERLAY_TASKS:
        case ScreenTaskControllerTypes.ONE_PASS_OPTIONS_OVERLAY_TASK:
        case ScreenTaskControllerTypes.RECORDING_OVERLAY_TASKS:
        case ScreenTaskControllerTypes.SLO_TASKS:
        case ScreenTaskControllerTypes.STOP_RECORDING_OVERLAY_TASKS:
        case ScreenTaskControllerTypes.SUBTITLE_AND_CC_LANGUAGE_OVERLAY:
        case ScreenTaskControllerTypes.VOD_RESUME_START_OVER_OVERLAY_TASKS:
            dispatch(onKeyDownOverlayDialog());
            break;

        case ScreenTaskControllerTypes.CHANNEL_OPTIONS_OVERLAY:
            dispatch(onKeyDownOverlayDialog());
            break;

        case ScreenTaskControllerTypes.DEFAULT_VIDEO_PLAYER_TASKS:
            dispatch(showFullInfo());
            break;

        case ScreenTaskControllerTypes.FULL_INFO:
            dispatch(onKeyDownFullInfoBanner());
            break;

        case ScreenTaskControllerTypes.ONE_LINE_GUIDE_TASKS:
            dispatch(onKeyDownOneLineGuide());
            break;

        case ScreenTaskControllerTypes.PC_PIN_OVERLAY_TASK:
            dispatch(onKeyDownPcPinOverlay());
            break;

        case ScreenTaskControllerTypes.RECORD_OVERLAY_TASKS:
            dispatch(onKeyDownOverlayDialog());
            break;
        default:
            break;
    }
};

export const onLongKeyDownVideoPlayerUi = () => (dispatch: Function, getState: Function) => {
    const { screenTaskController } = getState().screens;

    switch (screenTaskController) {
        case ScreenTaskControllerTypes.ONE_LINE_GUIDE_TASKS:
            dispatch(onLongKeyDownOneLineGuide());
            break;

        default:
    }
};

export const onKeyExitVideoPlayerUi = () => (dispatch: Function, getState: Function) => {
    const { isOverlayDialogActive } = getState().overlayDialog;

    if (isOverlayDialogActive || !VideoPlaybackController.getInstance().isSocu()) return;

    dispatch(resetPlaybackParamsVideoplayer());
    VideoPlaybackController.getInstance().switchToLiveTv();
};

export const onKeyGuideVideoPlayerUi = () => (dispatch: Function, getState: Function) => {
    const { screenTaskController } = getState().screens;

    switch (screenTaskController) {
        case ScreenTaskControllerTypes.DEFAULT_VIDEO_PLAYER_TASKS:
        case ScreenTaskControllerTypes.FULL_INFO:
        case ScreenTaskControllerTypes.SOCU_ERROR_OVERLAY_TASKS:
        case ScreenTaskControllerTypes.STANDARD_INFO:
        case ScreenTaskControllerTypes.PLAYER_ERROR_OVERLAY:
            break;

        case ScreenTaskControllerTypes.TRICKPLAY_MENU:
            dispatch(onKeyBackTrickplayMenu());
            break;

        case ScreenTaskControllerTypes.AUDIO_TRACK_OVERLAY:
            dispatch(onKeyBackAudioTrackOverlay());
            break;

        case ScreenTaskControllerTypes.PC_PIN_OVERLAY_TASK:
            dispatch(resetPCPinValuesAction());
            dispatch(hidePcPinOverlay());
            break;

        case ScreenTaskControllerTypes.SUBTITLE_AND_CC_LANGUAGE_OVERLAY:
            dispatch(onKeyBackSubtitleAndCCLanguageOverlay());
            break;

        case ScreenTaskControllerTypes.STILL_THERE_OSD_TASKS:
            // If audio/subtitle overlay will change to time based hide.
            // This code will still valid.
            dispatch(onKeyBackSubtitleAndCCLanguageOverlay());
            dispatch(onKeyBackAudioTrackOverlay());
            break;
        case ScreenTaskControllerTypes.VOD_RESUME_START_OVER_OVERLAY_TASKS:
            dispatch(onKeyBackVodResumeStartOverOverlay());
            break;

        default:
            break;
    }
    dispatch(onKeyBackVideoPlayer(true));
};

export const onKeyInfoVideoPlayerUi = () => (dispatch: Function, getState: Function) => {
    const { screenTaskController } = getState().screens;

    switch (screenTaskController) {
        case ScreenTaskControllerTypes.DEFAULT_VIDEO_PLAYER_TASKS:
            dispatch(showFullInfo());
            break;

        case ScreenTaskControllerTypes.TRICKPLAY_MENU:
            dispatch(onKeyInfoTrickplayMenu());
            break;

        case ScreenTaskControllerTypes.STANDARD_INFO:
            dispatch(onKeyInfoStandardInfoBanner());
            break;

        case ScreenTaskControllerTypes.ONE_LINE_GUIDE_TASKS:
            dispatch(onLongKeyOkOneLineGuide());

            break;
        default:
            break;
    }
};

export const onKeyLeftVideoPlayerUi = () => (dispatch: Function, getState: Function) => {
    const { screenTaskController } = getState().screens;

    switch (screenTaskController) {
        case ScreenTaskControllerTypes.CHANNEL_OPTIONS_OVERLAY:
            dispatch(onKeyLeftOverlayDialog());
            break;

        case ScreenTaskControllerTypes.DEFAULT_VIDEO_PLAYER_TASKS:
            dispatch(onKeyLeftVideoPlayer());
            break;

        case ScreenTaskControllerTypes.FULL_INFO:
            dispatch(onKeyLeftFullInfoBanner());
            break;

        case ScreenTaskControllerTypes.ONE_LINE_GUIDE_TASKS:
            dispatch(onKeyLeftOneLineGuide());
            break;

        case ScreenTaskControllerTypes.ONE_PASS_OPTIONS_OVERLAY_TASK:
            dispatch(onKeyLeftOnePassOptionsOverlay());
            break;

        case ScreenTaskControllerTypes.PC_PIN_OVERLAY_TASK:
            dispatch(onKeyLeftPcPinOverlay());
            break;

        case ScreenTaskControllerTypes.RECORDING_OVERLAY_TASKS:
            dispatch(onKeyLeftRecordingOverlay());
            break;

        case ScreenTaskControllerTypes.TRICKPLAY_MENU:
            dispatch(onKeyLeftTrickplayMenu());
            break;

        case ScreenTaskControllerTypes.STANDARD_INFO:
            dispatch(onKeyLeftStandardInfoBanner());
            break;

        default:
            break;
    }
};

export const onKeyLongLeftVideoPlayerUi = () => (dispatch: Function, getState: Function) => {
    const { screenTaskController } = getState().screens;

    switch (screenTaskController) {
        case ScreenTaskControllerTypes.TRICKPLAY_MENU:
            dispatch(onKeyLongLeftTrickplayMenu());
            break;
        default:
            break;
    }
};

export const clearLongPressIntervalVideoPlayerUi =
    () => (dispatch: Function, getState: Function) => {
        const { screenTaskController } = getState().screens;
        switch (screenTaskController) {
            case ScreenTaskControllerTypes.TRICKPLAY_MENU:
                dispatch(clearLongPressIntervalTrickplayMenu());
                break;

            default:
                break;
        }
    };

export const onKeyOkVideoPlayerUi = () => (dispatch: Function, getState: Function) => {
    const { screenTaskController } = getState().screens;
    const { currentSelectedCell } = getState().oneLineGuide;
    const { uiProperties } = getState().overlayDialog;
    const { overlayType } = uiProperties;

    switch (screenTaskController) {
        case ScreenTaskControllerTypes.AUDIO_TRACK_OVERLAY:
            dispatch(onKeyOkAudioTrackOverlay());
            break;

        case ScreenTaskControllerTypes.CANCEL_ONE_PASS_OVERLAY_TASKS:
            dispatch(onKeyOkDOCOverlay());
            break;

        case ScreenTaskControllerTypes.CANCEL_RECORDING_OVERLAY_TASKS:
            const recordingOverlayIds: TRecordingOverlayIds = {
                contentId: currentSelectedCell.contentId,
                offerId: currentSelectedCell.offerId,
            };
            dispatch(onKeyOkStopOrCancelRecording(recordingOverlayIds));
            break;

        case ScreenTaskControllerTypes.CHANNEL_OPTIONS_OVERLAY: {
            const { isEmpty } = getState().oneLineGuide;
            if (isEmpty) {
                dispatch(onKeyOkEmptyGuide(GuideType.ONE_LINE_GUIDE));
            } else {
                dispatch(onKeyOkChannelOptionsOverlayOneLineGuide());
            }
            break;
        }
        case ScreenTaskControllerTypes.CREATE_ONE_PASS_OVERLAY_TASKS:
            dispatch(onKeyOkCreateOnePassOverlay());
            break;

        case ScreenTaskControllerTypes.DEFAULT_VIDEO_PLAYER_TASKS:
            if (isAdultContentPlaybackActive()) dispatch(showPCPinOverlayForVideoPlayer());
            else dispatch(showTrickPlayMenu());
            break;

        case ScreenTaskControllerTypes.DELETE_RECORDING_OVERLAY_TASKS:
            dispatch(onKeyOkDeleteRecordingOverlay());
            break;

        case ScreenTaskControllerTypes.JUMP_CHANNEL_OVERLAY_TASKS: {
            dispatch(onKeyOkJumpChannelOverlay(onKeyChannelUpVideoPlayerUi));
            break;
        }

        case ScreenTaskControllerTypes.FULL_INFO:
            dispatch(onKeyOkFullInfoBanner());
            break;

        case ScreenTaskControllerTypes.LIVE_TV_INTERACTIVE_LAYER_TASKS:
            dispatch(onMouseClickVideoPlayerUiInteractiveLayerOperations());
            break;

        case ScreenTaskControllerTypes.MODIFY_ONE_PASS_OVERLAY_TASKS:
            dispatch(onKeyOkModifyOnePassOverlay());
            break;

        case ScreenTaskControllerTypes.ONE_LINE_GUIDE_TASKS:
            dispatch(onKeyOkOneLineGuide());
            break;
        case ScreenTaskControllerTypes.ONE_PASS_OPTIONS_OVERLAY_TASK:
            dispatch(onKeyOkOnePassOptionsOverlay());
            break;

        case ScreenTaskControllerTypes.PC_PIN_OVERLAY_TASK:
            dispatch(onKeyOkPcPinOverlay());
            break;

        case ScreenTaskControllerTypes.RECORD_OVERLAY_TASKS:
            dispatch(onKeyOkRecordOverlay());
            break;

        case ScreenTaskControllerTypes.RECORDING_OVERLAY_TASKS:
            dispatch(onKeyOkRecordingOverlay());
            break;

        case ScreenTaskControllerTypes.SLO_TASKS:
            dispatch(onKeyOkStorageLimitOverlay());
            break;

        case ScreenTaskControllerTypes.STOP_RECORDING_OVERLAY_TASKS:
            {
                const sharedPrefs = new SharedPreferences(
                    SharedPrefConstants.RECORDING_ACTION_PREF_KEY,
                );
                const contentId = sharedPrefs.getString(SharedPrefConstants.RECORDING_CONTENT_ID);
                const offerId = sharedPrefs.getString(SharedPrefConstants.RECORDING_OFFER_ID);
                dispatch(onKeyOkStopOrCancelRecording({ contentId, offerId }));
            }
            break;

        case ScreenTaskControllerTypes.SUBTITLE_AND_CC_LANGUAGE_OVERLAY:
            dispatch(onKeyOkSubtitleAndCCLanguageOverlay());
            break;

        case ScreenTaskControllerTypes.TRICKPLAY_MENU:
            dispatch(onKeyOkTrickplayMenu());
            break;

        case ScreenTaskControllerTypes.STANDARD_INFO:
            dispatch(onKeyOkStandardInfoBanner());
            break;
        case ScreenTaskControllerTypes.PLAYER_ERROR_OVERLAY:
            dispatch(onKeyOkErrorOverlay());
            dispatch(onKeyBackVideoPlayer());
            break;
        case ScreenTaskControllerTypes.JUMP_CHANNEL_ERROR_OVERLAY_TASKS:
            {
                dispatch(onKeyOkErrorOverlay());

                const wasErrorFromOneLineGuide =
                    overlayType === OverlayTypes.OLG_JUMP_CHANNEL_ERROR_OVERLAY;
                if (wasErrorFromOneLineGuide) {
                    dispatch(showOneLineGuide());
                } else {
                    dispatch(onKeyBackVideoPlayer());
                }
            }
            break;
        case ScreenTaskControllerTypes.SOCU_ERROR_OVERLAY_TASKS:
            dispatch(onKeyOkSocuErrorOverlay());
            break;
        case ScreenTaskControllerTypes.STILL_THERE_OSD_TASKS:
            dispatch(hideStillThereOsd());
            break;
        case ScreenTaskControllerTypes.VOD_RESUME_START_OVER_OVERLAY_TASKS:
            dispatch(onKeyOkVodResumeStartOverOverlayFromLiveTv());
            break;
        default:
            break;
    }
};

/**
 * Triggers on press and hold OK/SELECT event and performs the blow use cases:
 * - If any of the overlay is active, do nothing.
 * - If playing a SoCu content then switch to the live tv.
 * - If playing live tv and SoCu content is available then switch to available SoCu content.
 */
export const onLongKeyOkVideoPlayerUi = () => (dispatch: Function, getState: Function) => {
    const { isOverlayDialogActive } = getState().overlayDialog;
    const channel = VideoPlaybackController.getInstance().getPlayable()?.channel;

    const { screenTaskController } = getState().screens;
    if (screenTaskController === ScreenTaskControllerTypes.ONE_LINE_GUIDE_TASKS) {
        dispatch(onLongKeyOkOneLineGuide());
        return;
    }

    if (isOverlayDialogActive || (channel && !channel.isSubscribed)) return;

    // If SOCU/NDVR content is playing then switch to live tv.
    if (!VideoPlaybackController.getInstance().isLive()) {
        dispatch(hideInfoBanners());
        dispatch(resetPlaybackParamsVideoplayer());
        dispatch(hideInfoBanners());
        VideoPlaybackController.getInstance().switchToLiveTv();
        return;
    }

    const previewData = VideoPlaybackController.getInstance().getMetadata().offerPreview;
    const isSocuAvailable: boolean =
        DeviceFeatures.isSocuEnabled() && !!previewData?.isAvailableInSocu;
    const recordingStatus = previewData?.contentPreview?.recordingStatus;
    const isRecordingInProgress = recordingStatus === RecordingStatus.InProgressRecording || false;
    const watchAction = VideoPlaybackController.getInstance().getWatchAction();
    if (isSocuAvailable) {
        if (isAdultContentPlaybackActive()) {
            dispatch(hideInfoBanners());
            dispatch(showPCPinOverlayForVideoPlayer(prepareVodResumeStartOverOverlayFromLiveTv));
        } else dispatch(prepareVodResumeStartOverOverlayFromLiveTv());
        return;
    }
    if (isRecordingInProgress && watchAction) {
        if (isAdultContentPlaybackActive()) {
            dispatch(hideInfoBanners());
            dispatch(showPCPinOverlayForVideoPlayer(prepareVodResumeStartOverOverlayFromLiveTv));
        } else {
            const recordingId = watchAction?.kernel?.recordingId;
            const playable: IPlayable = {
                playbackContentType: PlaybackContentType.NDVR_LIVE,
                ndvrPlaybackModel: {
                    id: recordingId,
                    ndvrType: NdvrPlaybackType.RECORDING,
                },
            };
            VideoPlaybackController.getInstance().startPlayback(playable);
        }
    }
};

export const onKeyPauseVideoPlayerUi = () => (dispatch: Function, getState: Function) => {
    const { screenTaskController } = getState().screens;
    const { playbackState, isPlaying } = getState().videoPlayer;

    if (isAdultContentPlaybackActive()) return;

    switch (screenTaskController) {
        case ScreenTaskControllerTypes.TRICKPLAY_MENU:
            dispatch(updateStandardInfoBannerTimer());
            dispatch(setCurrentActiveIndexAction(MediaButtonTypes.PLAY_PAUSE_BUTTON_INDEX));
            dispatch(updateTrickPlayMenuTimer());

            dispatch(isPlayPauseRestricted(isPlaying ? TrickPlayType.PAUSE : TrickPlayType.PLAY));
            break;

        case ScreenTaskControllerTypes.DEFAULT_VIDEO_PLAYER_TASKS:
        case ScreenTaskControllerTypes.FULL_INFO:
        case ScreenTaskControllerTypes.STANDARD_INFO:
            if (screenTaskController === ScreenTaskControllerTypes.STANDARD_INFO)
                dispatch(hideStandardInfo());
            else if (screenTaskController === ScreenTaskControllerTypes.FULL_INFO)
                dispatch(hideFullInfo());
            if (playbackState === PlaybackState.PAUSE) {
                dispatch(onKeyPlayVideoPlayer());
            } else if (playbackState === PlaybackState.PLAY) {
                dispatch(showTrickPlayMenu());
                dispatch(onKeyPauseVideoPlayer());
            }
            break;

        default:
            break;
    }
};

export const onKeyPlayVideoPlayerUi = () => (dispatch: Function, getState: Function) => {
    const { screenTaskController } = getState().screens;
    const { playbackState } = getState().videoPlayer;

    if (isAdultContentPlaybackActive()) return;

    switch (screenTaskController) {
        case ScreenTaskControllerTypes.STANDARD_INFO:
        case ScreenTaskControllerTypes.DEFAULT_VIDEO_PLAYER_TASKS:
            if (playbackState === PlaybackState.PLAY) break;
            dispatch(onKeyPlayVideoPlayer());
            break;

        case ScreenTaskControllerTypes.FULL_INFO:
            if (playbackState === PlaybackState.PLAY) break;
            dispatch(onKeyPlayVideoPlayer());
            dispatch(hideFullInfo());
            break;

        case ScreenTaskControllerTypes.TRICKPLAY_MENU:
            dispatch(updateStandardInfoBannerTimer());
            dispatch(updateTrickPlayMenuTimer());
            dispatch(onKeyPlayVideoPlayer());
            break;

        case ScreenTaskControllerTypes.ONE_LINE_GUIDE_TASKS:
            dispatch(onKeyOkOneLineGuide());
            break;

        default:
            break;
    }
};

export const onKeyPlayPauseVideoPlayerUi = () => (dispatch: Function, getState: Function) => {
    const { screenTaskController } = getState().screens;
    const { playbackState, isPlaying } = getState().videoPlayer;

    if (isAdultContentPlaybackActive()) return;

    switch (screenTaskController) {
        case ScreenTaskControllerTypes.STANDARD_INFO:
        case ScreenTaskControllerTypes.FULL_INFO:
        case ScreenTaskControllerTypes.DEFAULT_VIDEO_PLAYER_TASKS:
            if (screenTaskController === ScreenTaskControllerTypes.FULL_INFO)
                dispatch(hideFullInfo());
            if (playbackState === PlaybackState.PLAY) {
                dispatch(showTrickPlayMenu());
                dispatch(onKeyPauseVideoPlayer());
            } else if (playbackState === PlaybackState.PAUSE) {
                dispatch(onKeyPlayVideoPlayer());
            }
            break;

        case ScreenTaskControllerTypes.TRICKPLAY_MENU:
            dispatch(updateStandardInfoBannerTimer());
            dispatch(updateTrickPlayMenuTimer());
            dispatch(setCurrentActiveIndexAction(MediaButtonTypes.PLAY_PAUSE_BUTTON_INDEX));
            dispatch(isPlayPauseRestricted(isPlaying ? TrickPlayType.PAUSE : TrickPlayType.PLAY));
            break;

        default:
            break;
    }
};

export const onKeyStopVideoPlayerUi = () => (dispatch: Function, getState: Function) => {
    const { screenTaskController } = getState().screens;

    if (isAdultContentPlaybackActive()) return;

    switch (screenTaskController) {
        case ScreenTaskControllerTypes.DEFAULT_VIDEO_PLAYER_TASKS:
            dispatch(onKeyStopVideoPlayer());
            break;

        case ScreenTaskControllerTypes.FULL_INFO:
            dispatch(onKeyStopVideoPlayer());
            dispatch(hideFullInfo());
            break;

        case ScreenTaskControllerTypes.TRICKPLAY_MENU:
            dispatch(onKeyStopVideoPlayer());
            dispatch(hideTrickPlayMenu());
            break;

        case ScreenTaskControllerTypes.STANDARD_INFO:
            dispatch(onKeyStopVideoPlayer());
            dispatch(hideStandardInfo());
            break;

        default:
            break;
    }
};

export const onKeyFastForwardVideoPlayerUi = () => (dispatch: Function, getState: Function) => {
    const { screenTaskController } = getState().screens;

    if (isAdultContentPlaybackActive()) return;

    switch (screenTaskController) {
        case ScreenTaskControllerTypes.STANDARD_INFO:
        case ScreenTaskControllerTypes.DEFAULT_VIDEO_PLAYER_TASKS:
            dispatch(showTrickPlayMenu());
            dispatch(onKeyFastForwardVideoPlayer());
            break;

        case ScreenTaskControllerTypes.FULL_INFO:
            dispatch(hideFullInfo());
            dispatch(showTrickPlayMenu());
            dispatch(onKeyFastForwardVideoPlayer());
            break;

        case ScreenTaskControllerTypes.TRICKPLAY_MENU:
            dispatch(updateTrickPlayMenuTimer());
            dispatch(updateStandardInfoBannerTimer());
            dispatch(onKeyFastForwardVideoPlayer());
            break;

        default:
            break;
    }
};

export const onKeyRewindVideoPlayerUi = () => (dispatch: Function, getState: Function) => {
    const { screenTaskController } = getState().screens;

    if (isAdultContentPlaybackActive()) return;

    switch (screenTaskController) {
        case ScreenTaskControllerTypes.STANDARD_INFO:
        case ScreenTaskControllerTypes.DEFAULT_VIDEO_PLAYER_TASKS:
            dispatch(showTrickPlayMenu());
            dispatch(onKeyRewindVideoPlayer());
            break;

        case ScreenTaskControllerTypes.FULL_INFO:
            dispatch(hideFullInfo());
            dispatch(showTrickPlayMenu());
            dispatch(onKeyRewindVideoPlayer());
            break;

        case ScreenTaskControllerTypes.TRICKPLAY_MENU:
            dispatch(updateTrickPlayMenuTimer());
            dispatch(updateStandardInfoBannerTimer());
            dispatch(onKeyRewindVideoPlayer());
            break;

        default:
            break;
    }
};

export const onKeyReplayVideoPlayerUi = () => (dispatch: Function, getState: Function) => {
    const { screenTaskController } = getState().screens;

    if (isAdultContentPlaybackActive()) return;

    switch (screenTaskController) {
        case ScreenTaskControllerTypes.STANDARD_INFO:
        case ScreenTaskControllerTypes.DEFAULT_VIDEO_PLAYER_TASKS:
            dispatch(showTrickPlayMenu());
            dispatch(onKeyReplayVideoPlayer());
            break;

        case ScreenTaskControllerTypes.FULL_INFO:
            dispatch(hideFullInfo());
            dispatch(showTrickPlayMenu());
            dispatch(onKeyReplayVideoPlayer());
            break;

        case ScreenTaskControllerTypes.TRICKPLAY_MENU:
            dispatch(updateTrickPlayMenuTimer());
            dispatch(updateStandardInfoBannerTimer());
            dispatch(onKeyReplayVideoPlayer());
            break;

        default:
            break;
    }
};

export const onKeyAdvanceVideoPlayerUi = () => (dispatch: Function, getState: Function) => {
    const { screenTaskController } = getState().screens;

    if (isAdultContentPlaybackActive()) return;

    switch (screenTaskController) {
        case ScreenTaskControllerTypes.STANDARD_INFO:
        case ScreenTaskControllerTypes.DEFAULT_VIDEO_PLAYER_TASKS:
            dispatch(showTrickPlayMenu());
            dispatch(onKeyAdvanceVideoPlayer());
            break;

        case ScreenTaskControllerTypes.FULL_INFO:
            dispatch(hideFullInfo());
            dispatch(showTrickPlayMenu());
            dispatch(onKeyAdvanceVideoPlayer());
            break;

        case ScreenTaskControllerTypes.TRICKPLAY_MENU:
            dispatch(updateTrickPlayMenuTimer());
            dispatch(updateStandardInfoBannerTimer());
            dispatch(onKeyAdvanceVideoPlayer());
            break;

        default:
            break;
    }
};

export const onKeyRightVideoPlayerUi = () => (dispatch: Function, getState: Function) => {
    const { screenTaskController } = getState().screens;

    switch (screenTaskController) {
        case ScreenTaskControllerTypes.CHANNEL_OPTIONS_OVERLAY:
            dispatch(onKeyRightOverlayDialog());
            break;

        case ScreenTaskControllerTypes.DEFAULT_VIDEO_PLAYER_TASKS:
            dispatch(onKeyRightVideoPlayer());
            break;

        case ScreenTaskControllerTypes.FULL_INFO:
            dispatch(onKeyRightFullInfoBanner());
            break;

        case ScreenTaskControllerTypes.ONE_LINE_GUIDE_TASKS:
            dispatch(onKeyRightOneLineGuide());
            break;

        case ScreenTaskControllerTypes.ONE_PASS_OPTIONS_OVERLAY_TASK:
            dispatch(onKeyRightOnePassOptionsOverlay());
            break;

        case ScreenTaskControllerTypes.PC_PIN_OVERLAY_TASK:
            dispatch(onKeyRightPcPinOverlay());
            break;

        case ScreenTaskControllerTypes.RECORDING_OVERLAY_TASKS:
            dispatch(onKeyRightRecordingOverlay());
            break;

        case ScreenTaskControllerTypes.TRICKPLAY_MENU:
            dispatch(onKeyRightTrickplayMenu());
            break;

        case ScreenTaskControllerTypes.STANDARD_INFO:
            dispatch(onKeyRightStandardInfoBanner());
            break;

        default:
            break;
    }
};

export const onKeyLongRightVideoPlayerUi = () => (dispatch: Function, getState: Function) => {
    const { screenTaskController } = getState().screens;

    switch (screenTaskController) {
        case ScreenTaskControllerTypes.TRICKPLAY_MENU:
            dispatch(onKeyLongRightTrickplayMenu());
            break;

        default:
            break;
    }
};

export const onLongKeyStopVideoPlayerUi = () => (dispatch: Function, getState: Function) => {
    const { screenTaskController } = getState().screens;

    switch (screenTaskController) {
        case ScreenTaskControllerTypes.ONE_LINE_GUIDE_TASKS:
            dispatch(onLongKeyStopOneLineGuide());
            break;

        default:
    }
};

export const onKeyTivoVideoPlayerUi = () => (dispatch: Function, getState: Function) => {
    const { screenTaskController } = getState().screens;

    switch (screenTaskController) {
        case ScreenTaskControllerTypes.AUDIO_TRACK_OVERLAY:
            break;

        case ScreenTaskControllerTypes.DEFAULT_VIDEO_PLAYER_TASKS:
            break;

        case ScreenTaskControllerTypes.FULL_INFO:
            break;

        case ScreenTaskControllerTypes.SUBTITLE_AND_CC_LANGUAGE_OVERLAY:
            break;

        case ScreenTaskControllerTypes.TRICKPLAY_MENU:
            break;

        default:
            break;
    }
};

export const onKeyUpVideoPlayerUi = () => (dispatch: Function, getState: Function) => {
    const { screenTaskController } = getState().screens;

    switch (screenTaskController) {
        case ScreenTaskControllerTypes.AUDIO_TRACK_OVERLAY:
        case ScreenTaskControllerTypes.CANCEL_ONE_PASS_OVERLAY_TASKS:
        case ScreenTaskControllerTypes.CANCEL_RECORDING_OVERLAY_TASKS:
        case ScreenTaskControllerTypes.CREATE_ONE_PASS_OVERLAY_TASKS:
        case ScreenTaskControllerTypes.DELETE_RECORDING_OVERLAY_TASKS:
        case ScreenTaskControllerTypes.JUMP_CHANNEL_OVERLAY_TASKS:
        case ScreenTaskControllerTypes.MODIFY_ONE_PASS_OVERLAY_TASKS:
        case ScreenTaskControllerTypes.ONE_PASS_OPTIONS_OVERLAY_TASK:
        case ScreenTaskControllerTypes.RECORDING_OVERLAY_TASKS:
        case ScreenTaskControllerTypes.SLO_TASKS:
        case ScreenTaskControllerTypes.STOP_RECORDING_OVERLAY_TASKS:
        case ScreenTaskControllerTypes.SUBTITLE_AND_CC_LANGUAGE_OVERLAY:
        case ScreenTaskControllerTypes.VOD_RESUME_START_OVER_OVERLAY_TASKS:
            dispatch(onKeyUpOverlayDialog());
            break;
        case ScreenTaskControllerTypes.CHANNEL_OPTIONS_OVERLAY:
            dispatch(onKeyUpOverlayDialog());
            break;
        case ScreenTaskControllerTypes.DEFAULT_VIDEO_PLAYER_TASKS:
            dispatch(showOneLineGuide());
            break;

        case ScreenTaskControllerTypes.PC_PIN_OVERLAY_TASK:
            dispatch(onKeyUpPcPinOverlay());
            break;

        case ScreenTaskControllerTypes.ONE_LINE_GUIDE_TASKS:
            dispatch(onKeyUpOneLineGuide());
            break;
        case ScreenTaskControllerTypes.RECORD_OVERLAY_TASKS:
            dispatch(onKeyUpOverlayDialog());
            break;
        default:
            break;
    }
};

export const onLongKeyUpVideoPlayerUi = () => (dispatch: Function, getState: Function) => {
    const { screenTaskController } = getState().screens;

    switch (screenTaskController) {
        case ScreenTaskControllerTypes.ONE_LINE_GUIDE_TASKS:
            dispatch(onLongKeyUpOneLineGuide());
            break;

        default:
    }
};

export const onMouseClickVideoPlayerUi = () => (dispatch: Function, getState: Function) => {
    const { inputFactory, screens, interactiveLayer, overlayHighlight, oneLineGuide } = getState();
    const { value } = inputFactory;
    const { screenTaskController } = screens;
    const { isEmpty } = oneLineGuide;
    const GLOBAL_MOUSE_TRAP = 'HideChannelOptionsOverlay';
    const { activeComponent } = interactiveLayer;
    const { verticalPosition } = overlayHighlight;

    if (value === Constants.ROOT_INTERACTIVE_LAYER || value === Constants.DIMMER) {
        dispatch(onMouseClickRootInteractiveLayerOperations());
        return;
    }

    switch (screenTaskController) {
        case ScreenTaskControllerTypes.CANCEL_ONE_PASS_OVERLAY_TASKS:
            dispatch(onMouseClickDOCOptionsOverlay());
            break;
        case ScreenTaskControllerTypes.CREATE_ONE_PASS_OVERLAY_TASKS:
            dispatch(onMouseClickCreateOnePassOverlay());
            break;
        case ScreenTaskControllerTypes.MODIFY_ONE_PASS_OVERLAY_TASKS:
            dispatch(onMouseClickModifyOnePassOverlay());
            break;
        case ScreenTaskControllerTypes.ONE_PASS_OPTIONS_OVERLAY_TASK:
            dispatch(onMouseClickOnePassOptionsOverlay());
            break;

        case ScreenTaskControllerTypes.RECORD_OVERLAY_TASKS:
            dispatch(onMouseClickRecordOverlay());
            break;

        case ScreenTaskControllerTypes.RECORDING_OVERLAY_TASKS:
            dispatch(onMouseClickRecordingOverlay());
            break;

        case ScreenTaskControllerTypes.SLO_TASKS:
            dispatch(onMouseClickStorageLimitOverlay());
            break;

        case ScreenTaskControllerTypes.STOP_RECORDING_OVERLAY_TASKS:
            dispatch(onMouseClickStopOrCancelRecording());
            break;

        case ScreenTaskControllerTypes.JUMP_CHANNEL_OVERLAY_TASKS: {
            dispatch(onMouseClickJumpChannelOverlay(onKeyChannelUpVideoPlayerUi));
            break;
        }

        case ScreenTaskControllerTypes.CHANNEL_OPTIONS_OVERLAY: {
            if (isEmpty) {
                dispatch(onMouseClickEmptyGuide(GuideType.ONE_LINE_GUIDE));
                // This code is here because in all other cases we decide in this file what happens
                // when the overlay closes. Ideally we would provide a function for each action in
                // the overlay to be executed when the action is selected. But since the overlay
                // doesn't work this way and we don't want to add this logic in the overlay this
                // seems to be the recommended way
                if (verticalPosition === 0) {
                    // Go To Previous Screen if clicked
                    dispatch(showPreviousScreen());
                } else if (
                    // The channels filter is clicked
                    verticalPosition === 1 &&
                    value !== GLOBAL_MOUSE_TRAP &&
                    activeComponent !== InteractiveComponents.WEST &&
                    activeComponent !== InteractiveComponents.EAST
                ) {
                    dispatch(showOneLineGuide());
                }
            } else {
                dispatch(onMouseClickChannelOptionsOverlay());
            }

            return;
        }

        case ScreenTaskControllerTypes.ONE_LINE_GUIDE_TASKS:
            dispatch(onMouseClickOneLineGuide());
            return;

        case ScreenTaskControllerTypes.PC_PIN_OVERLAY_TASK:
            dispatch(onMouseClickPcPinOverlay());
            break;

        default:
            dispatch(onKeyOkVideoPlayerUi());
            break;
    }
};

export const onMouseLongOkVideoPlayerUi = () => (dispatch: Function, getState: Function) => {
    const { screenTaskController } = getState().screens;
    switch (screenTaskController) {
        case ScreenTaskControllerTypes.ONE_LINE_GUIDE_TASKS:
            dispatch(onLongKeyOkOneLineGuide());
            break;
        default:
    }
};

export const onMouseOverVideoPlayerUi = () => (dispatch: Function, getState: Function) => {
    const { value } = getState().inputFactory;
    if (value === Constants.ROOT_INTERACTIVE_LAYER) return;
    const screenTaskControllerVal = getState().screens.screenTaskController;
    if (screenTaskControllerVal === ScreenTaskControllerTypes.FULL_INFO) {
        dispatch(onMouseOverVideoPlayerUiOperations());
        dispatch(updateFullInfoBannerTimer());
    }
    const { screenTaskController } = getState().screens;
    switch (screenTaskController) {
        case ScreenTaskControllerTypes.AUDIO_TRACK_OVERLAY:
        case ScreenTaskControllerTypes.CANCEL_ONE_PASS_OVERLAY_TASKS:
        case ScreenTaskControllerTypes.CREATE_ONE_PASS_OVERLAY_TASKS:
        case ScreenTaskControllerTypes.MODIFY_ONE_PASS_OVERLAY_TASKS:
        case ScreenTaskControllerTypes.ONE_PASS_OPTIONS_OVERLAY_TASK:
        case ScreenTaskControllerTypes.CHANNEL_OPTIONS_OVERLAY:
        case ScreenTaskControllerTypes.DELETE_RECORDING_OVERLAY_TASKS:
        case ScreenTaskControllerTypes.JUMP_CHANNEL_OVERLAY_TASKS:
        case ScreenTaskControllerTypes.PLAYER_ERROR_OVERLAY:
        case ScreenTaskControllerTypes.RECORD_OVERLAY_TASKS:
        case ScreenTaskControllerTypes.SLO_TASKS:
        case ScreenTaskControllerTypes.RECORDING_OVERLAY_TASKS:
        case ScreenTaskControllerTypes.STOP_RECORDING_OVERLAY_TASKS:
        case ScreenTaskControllerTypes.SOCU_ERROR_OVERLAY_TASKS:
        case ScreenTaskControllerTypes.SUBTITLE_AND_CC_LANGUAGE_OVERLAY:
        case ScreenTaskControllerTypes.VOD_RESUME_START_OVER_OVERLAY_TASKS:
        case ScreenTaskControllerTypes.JUMP_CHANNEL_ERROR_OVERLAY_TASKS:
            dispatch(onMouseOverOverlayDialogOperations());
            break;
        case ScreenTaskControllerTypes.FULL_INFO:
            dispatch(setActionsStripHighlightBorderActive(true));
            break;

        case ScreenTaskControllerTypes.TRICKPLAY_MENU:
            dispatch(setMediaButtonMouseOverIndex(Number(value)));
            dispatch(updateTrickPlayMenuTimer());
            dispatch(updateStandardInfoBannerTimer());
            break;

        case ScreenTaskControllerTypes.PC_PIN_OVERLAY_TASK:
            dispatch(onMouseOverPcPinOverlay());
            break;

        default:
    }
};

export const onMouseOutVideoPlayerUi = () => (dispatch: Function, getState: Function) => {
    const screenTaskControllerVal = getState().screens.screenTaskController;
    if (screenTaskControllerVal === ScreenTaskControllerTypes.LIVE_TV_INTERACTIVE_LAYER_TASKS)
        dispatch(onMouseOutVideoPlayerUiOperations());
    const { screenTaskController } = getState().screens;
    switch (screenTaskController) {
        case ScreenTaskControllerTypes.AUDIO_TRACK_OVERLAY:
        case ScreenTaskControllerTypes.CANCEL_ONE_PASS_OVERLAY_TASKS:
        case ScreenTaskControllerTypes.CREATE_ONE_PASS_OVERLAY_TASKS:
        case ScreenTaskControllerTypes.MODIFY_ONE_PASS_OVERLAY_TASKS:
        case ScreenTaskControllerTypes.ONE_PASS_OPTIONS_OVERLAY_TASK:
        case ScreenTaskControllerTypes.CHANNEL_OPTIONS_OVERLAY:
        case ScreenTaskControllerTypes.DELETE_RECORDING_OVERLAY_TASKS:
        case ScreenTaskControllerTypes.JUMP_CHANNEL_OVERLAY_TASKS:
        case ScreenTaskControllerTypes.PLAYER_ERROR_OVERLAY:
        case ScreenTaskControllerTypes.SLO_TASKS:
        case ScreenTaskControllerTypes.RECORD_OVERLAY_TASKS:
        case ScreenTaskControllerTypes.RECORDING_OVERLAY_TASKS:
        case ScreenTaskControllerTypes.STOP_RECORDING_OVERLAY_TASKS:
        case ScreenTaskControllerTypes.SOCU_ERROR_OVERLAY_TASKS:
        case ScreenTaskControllerTypes.SUBTITLE_AND_CC_LANGUAGE_OVERLAY:
        case ScreenTaskControllerTypes.VOD_RESUME_START_OVER_OVERLAY_TASKS:
        case ScreenTaskControllerTypes.JUMP_CHANNEL_ERROR_OVERLAY_TASKS:
            dispatch(onMouseOutOverlayDialogOperations());
            break;
        case ScreenTaskControllerTypes.FULL_INFO:
            dispatch(setActionsStripHighlightBorderActive(false));
            break;
        case ScreenTaskControllerTypes.TRICKPLAY_MENU:
            dispatch(setMediaButtonMouseOverIndex(-1));
            break;
        case ScreenTaskControllerTypes.PC_PIN_OVERLAY_TASK:
            dispatch(onMouseOutPcPinOverlay());
            break;
        default:
    }
};

export const onMouseWheelVideoPlayerUi =
    (wheelDeltaY: number) => (dispatch: Function, getState: Function) => {
        const { screenTaskController } = getState().screens;
        switch (screenTaskController) {
            case ScreenTaskControllerTypes.ONE_LINE_GUIDE_TASKS:
                dispatch(onMouseWheelOneLineGuide(wheelDeltaY));
                break;
            case ScreenTaskControllerTypes.RECORD_OVERLAY_TASKS:
                dispatch(onMouseWheelRecordOverlay(wheelDeltaY));
                break;
            default:
        }
    };

export const onMouseOkLongPressOneLineGuide = () => (dispatch: Function, getState: Function) => {
    const { screenTaskController } = getState().screens;

    switch (screenTaskController) {
        case ScreenTaskControllerTypes.ONE_LINE_GUIDE_TASKS:
            dispatch(onLongClickOneLineGuide());
            break;
        default:
    }
};

export const onMouseOkLongPressStopOneLineGuide =
    () => (dispatch: Function, getState: Function) => {
        const { screenTaskController } = getState().screens;

        switch (screenTaskController) {
            case ScreenTaskControllerTypes.ONE_LINE_GUIDE_TASKS:
                dispatch(onLongClickStopOneLineGuide());
                break;
            default:
        }
    };

export const onLongKeyChannelUpStartVideoPlayerUi =
    () => (dispatch: Function, getState: Function) => {
        const { screenTaskController } = getState().screens;

        switch (screenTaskController) {
            case ScreenTaskControllerTypes.ONE_LINE_GUIDE_TASKS:
                dispatch(onLongKeyUpOneLineGuide());
                break;

            default:
        }
    };

export const onLongKeyChannelDownStartVideoPlayerUi =
    () => (dispatch: Function, getState: Function) => {
        const { screenTaskController } = getState().screens;

        switch (screenTaskController) {
            case ScreenTaskControllerTypes.ONE_LINE_GUIDE_TASKS:
                dispatch(onLongKeyDownOneLineGuide());
                break;

            default:
        }
    };
